import {
  createSignal,
  createEffect,
  createRef,
  createTransaction,
  untrack,
} from "./signal";

type ReturnTypeTuple<T extends any[]> = {
  [Index in keyof T]: Exclude<ReturnType<T[Index]>, undefined>;
};

export type ServiceProvider<T, P extends any[]> = (() => T | undefined) & {
  readonly id: string | symbol;
  deps: () => P;
  promise: () => Promise<T>;
  register: ServiceRegister<T, P>;
  isRegistered: () => boolean;
};

export type ServiceFactory<T, P extends any[]> = (
  ...deps: ReturnTypeTuple<P>
) => T | Promise<T>;

export type ServiceDisposer<T> = (service: T) => void;

export type ServiceRegister<T, P extends any[]> = (
  factory: ServiceFactory<T, P>,
  disposer?: ServiceDisposer<T>
) => void;

export type ServiceWithDepsInjection<T> = {
  dependsOn: <P extends ServiceProvider<any, any>[]>(
    deps: [...P]
  ) => ServiceProvider<T, P>;
};

export function createService<T>(): ServiceWithDepsInjection<T> {
  return {
    dependsOn: (deps) => {
      const [getService, setService] = createSignal<T>(null as T);
      const promiseRef = createRef<any>();
      const resolverRef = createRef<any>();
      const factoryRef = createRef<any>();
      const disposerRef = createRef<ServiceDisposer<T>>();

      promiseRef(
        new Promise((resolve) => {
          resolverRef(resolve);
        })
      );

      const instantiate = (factory: any) => {
        const resolvedDeps = resolveDeps(deps);
        return untrack(() => {
          if (
            typeof factory !== "function" ||
            resolvedDeps.some((dep: any) => !dep)
          ) {
            return;
          }
          
          if (getService()) {
            throw new Error('tttt')
          }

          then(factory(...resolvedDeps), (service) => {
            createTransaction(() => {
              setService(service);
            });
          });
        });
      };

      const registerService = (factory: any, disposer: any) => {
        factoryRef(factory);
        disposerRef(disposer);
        instantiate(factoryRef());
      };

      createEffect(() => {
        instantiate(factoryRef());
      });

      createEffect(() => {
        const service = getService();
        return untrack(() => {
          if (!service) return;

          const lastPromise = promiseRef();
          resolverRef()(service);
          promiseRef(Promise.resolve(service));

          return () => {
            lastPromise.finally(() => {
              disposerRef()?.(service);
            });
          };
        });
      });

      const service = Object.assign(getService, {
        deps: () => deps,
        promise: () => promiseRef(),
        register: registerService,
        isRegistered: () => !!factoryRef(),
      });

      return service as ServiceProvider<T, typeof deps>;
    },
  };
}

export function createLazyService<T>(): ServiceWithDepsInjection<T> {
  return {
    dependsOn: (deps) => {
      const service = createService<T>().dependsOn(deps);
      const registerRef =
        createRef<
          [ServiceFactory<T, typeof deps>, ServiceDisposer<T> | undefined]
        >();

      let isRegistered = false;

      const registerOnDemand = () => {
        if (!service() && registerRef() && !isRegistered) {
          service.register(...registerRef());
          isRegistered = true;
        }
      };

      const getServiceLazy = () => {
        registerOnDemand();
        return service();
      };

      Object.assign(getServiceLazy, {
        deps: () => deps,
        promise: () => {
          registerOnDemand();
          return service.promise();
        },
        register: (factory, disposer) => registerRef([factory, disposer]),
        isRegistered: () => !!registerRef(),
      });

      return getServiceLazy as ServiceProvider<T, typeof deps>;
    },
  };
}

function resolveDeps(deps: (() => any)[] = []) {
  return deps.map((dep: () => any) => dep());
}

type Constructor<T extends any[], R> = new (...args: T) => R;

export function callable<T extends any[], R>(
  constructor: Constructor<T, R>
): (...args: T) => R {
  return (...args: T) => {
    return new constructor(...args);
  };
}

function then<T>(
  maybePromise: PromiseLike<T> | T,
  callback: (data: T) => void
) {
  if (typeof (maybePromise as PromiseLike<T>)?.then === "function") {
    (maybePromise as PromiseLike<T>).then(callback);
    return;
  }

  return callback(maybePromise as T);
}
